import { supabase } from "@/integrations/supabase/client";
import type { NovaObraData, ReportDefaults } from "@/hooks/useNovaObra";
import type { ObraRecord } from "@/lib/obraCache";

// Map a database row (snake_case) into the app's ObraRecord (camelCase),
// which is the same shape used by the legacy localStorage cache.
function rowToObraRecord(row: any): ObraRecord {
  return {
    id: row.id,
    nomeObra: row.nome_obra ?? "",
    numeroObra: row.numero_obra ?? "",
    responsaveisObra: row.responsaveis_obra ?? [],
    clienteObra: row.cliente_obra ?? "",
    clientesResponsaveis: row.clientes_responsaveis ?? [],
    dataInicio: row.data_inicio ?? undefined,
    previsaoTermino: row.previsao_termino ?? undefined,
    statusObra: row.status_obra ?? "",
    endereco: {
      rua: row.endereco_rua ?? "",
      numero: row.endereco_numero ?? "",
      complemento: row.endereco_complemento ?? "",
      cidade: row.endereco_cidade ?? "",
      estado: row.endereco_estado ?? "",
      cep: row.endereco_cep ?? "",
      referencia: row.endereco_referencia ?? "",
      observacao: row.endereco_observacao ?? "",
    },
    reportDefaults: (row.report_defaults as ReportDefaults) ?? {
      condicoesClimaticas: true,
      maoDeObra: true,
      equipamentos: true,
      atividades: true,
      ocorrencias: true,
      materiais: true,
      comentarios: true,
      fotos: true,
      videos: true,
      anexos: true,
    },
    // Config de aprovação persistida em obras.signature_settings (jsonb).
    // Default apenas para obras criadas antes de a coluna ser usada.
    aprovacao: (row.signature_settings as NovaObraData["aprovacao"]) ?? {
      tipo: "simples",
      modo: "sem-hierarquia",
      participantes: [],
    },
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function novaObraToInsert(data: NovaObraData, userId: string) {
  const toIso = (d?: Date | string) =>
    d instanceof Date ? d.toISOString() : d ?? null;
  return {
    user_id: userId,
    nome_obra: data.nomeObra,
    numero_obra: data.numeroObra || null,
    cliente_obra: data.clienteObra || null,
    clientes_responsaveis: data.clientesResponsaveis ?? [],
    responsaveis_obra: data.responsaveisObra ?? [],
    data_inicio: toIso(data.dataInicio),
    previsao_termino: toIso(data.previsaoTermino),
    status_obra: data.statusObra || null,
    endereco_rua: data.endereco.rua || null,
    endereco_numero: data.endereco.numero || null,
    endereco_complemento: data.endereco.complemento || null,
    endereco_cidade: data.endereco.cidade || null,
    endereco_estado: data.endereco.estado || null,
    endereco_cep: data.endereco.cep || null,
    endereco_referencia: data.endereco.referencia || null,
    endereco_observacao: data.endereco.observacao || null,
    report_defaults: data.reportDefaults as any,
    signature_settings: (data.aprovacao ?? null) as any,
  };
}

export async function listObrasFromSupabase(): Promise<ObraRecord[]> {
  const { data, error } = await supabase
    .from("obras")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) {
    console.error("Erro ao listar obras:", error);
    throw error;
  }
  return (data ?? []).map(rowToObraRecord);
}

export async function createObraInSupabase(
  data: NovaObraData,
  userId: string
): Promise<ObraRecord> {
  const payload = novaObraToInsert(data, userId);
  const { data: inserted, error } = await supabase
    .from("obras")
    .insert(payload)
    .select("*")
    .single();
  if (error) {
    console.error("Erro ao criar obra:", error);
    throw error;
  }
  return rowToObraRecord(inserted);
}

export async function updateObraInSupabase(
  id: string,
  data: NovaObraData
): Promise<ObraRecord> {
  const toIso = (d?: Date | string) =>
    d instanceof Date ? d.toISOString() : d ?? null;
  const { data: updated, error } = await supabase
    .from("obras")
    .update({
      nome_obra: data.nomeObra,
      numero_obra: data.numeroObra || null,
      cliente_obra: data.clienteObra || null,
      clientes_responsaveis: data.clientesResponsaveis ?? [],
      responsaveis_obra: data.responsaveisObra ?? [],
      data_inicio: toIso(data.dataInicio),
      previsao_termino: toIso(data.previsaoTermino),
      status_obra: data.statusObra || null,
      endereco_rua: data.endereco?.rua || null,
      endereco_numero: data.endereco?.numero || null,
      endereco_complemento: data.endereco?.complemento || null,
      endereco_cidade: data.endereco?.cidade || null,
      endereco_estado: data.endereco?.estado || null,
      endereco_cep: data.endereco?.cep || null,
      endereco_referencia: data.endereco?.referencia || null,
      endereco_observacao: data.endereco?.observacao || null,
      report_defaults: data.reportDefaults as any,
      signature_settings: (data.aprovacao ?? null) as any,
    })
    .eq("id", id)
    .select("*")
    .single();
  if (error) {
    console.error("Erro ao atualizar obra:", error);
    throw error;
  }
  return rowToObraRecord(updated);
}

export async function deleteObraFromSupabase(id: string): Promise<void> {
  const { error } = await supabase.from("obras").delete().eq("id", id);
  if (error) {
    console.error("Erro ao deletar obra:", error);
    throw error;
  }
}
